import { Component, Input, NgZone, OnInit } from '@angular/core';
import { get } from 'lodash';
import { MessageService, SelectItem } from 'primeng';
import { Message } from 'src/app/classes/Message';
import { LoggerService } from 'src/app/services';
import { TaggedLogger } from 'src/app/services/taggedLogger';
import {
  IChecklistTaskTypeStep,
  IChecklistTaskTypeStepDetail,
  IGenericTaskStep,
} from '../../../interfaces/checklist-task-type';
import { IOrganisationSpecificStepGroup } from '../../../interfaces/organisation-specific-steps';
import { ITaskStep } from '../../../interfaces/task-info';
import { OrganisationSpecificStepsService } from '../../../services';
import { TaskTypeService } from '../../../services';
import { Loader } from '../../../utils/loader';
import { move } from '../../../utils/move';
import { StepType } from '../../job-control/step.type';

/**
 * Emitted by onRowReorder (p-table) when a row is reordered
 */
export interface ReorderEvent {
  /**
   * The index dragged
   */
  dragIndex: number;
  /**
   * Where it was dropped
   */
  dropIndex: number;
}

/**
 * Task Types tab of Enhanced Checklist Page
 */
@Component({
  selector: 'app-organisation-specific-step-task-types',
  templateUrl: './organisation-specific-step-task-types.component.html',
  styleUrls: ['./organisation-specific-step-task-types.component.scss'],
})
export class OrganisationSpecificStepTaskTypesComponent implements OnInit {
  private readonly logger: TaggedLogger;
  private readonly loader: Loader;
  checklistDisabled: boolean = false;

  get isBusy() {
    if (!this.loader) return true; // If we've not set this up yet, we're busy

    return this.loader.isBusy;
  }

  /**
   * todo, if this is still slightly ropey on updates then we probably want to additionally use a BHS, but this should work.
   * @private
   */
  private _selectedChecklist: IOrganisationSpecificStepGroup;
  @Input() set selectedChecklist(checklist: IOrganisationSpecificStepGroup) {
    this._selectedChecklist = checklist;
    this.checklistDisabled = checklist.inactiveDateTime != null;
    this.refreshChecklistTaskTypeSteps();
  }

  get selectedChecklist(): IOrganisationSpecificStepGroup {
    return this._selectedChecklist;
  }

  get isLocked(): boolean {
    return get(this.selectedChecklist, 'locked');
  }

  _selectedTaskTypeId: number;
  set selectedTaskTypeId(id: number) {
    this.logger.info('Task Type Changed to', id);
    this._selectedTaskTypeId = id;
    // note: happy to drop the promise here thanks to .loader
    this.onSelectedTaskTypeChange();
  }

  get selectedTaskTypeId(): number {
    return this._selectedTaskTypeId;
  }

  private selectedTaskTypeSteps: ITaskStep[];
  private checklistTaskTypeSteps: IChecklistTaskTypeStep;
  private _taskTypeEnabled: boolean;
  get taskTypeEnabled(): boolean {
    return this._taskTypeEnabled;
  }

  set taskTypeEnabled(enabled: boolean) {
    this._taskTypeEnabled = enabled;
    // Ensures we save a new mapping when enabled is changed
    this.logger.debug('Saving initial mapping because we dont have one yet');
    this.saveChecklistMapping();
  }

  taskTypes: SelectItem[];
  autoApplyChecklist: boolean = false;

  /**
   * The steps, sorted on this task
   * @private
   */
  private _steps: IGenericTaskStep[];
  set steps(steps: IGenericTaskStep[]) {
    this._steps = steps;
  }

  get steps(): IGenericTaskStep[] {
    return this._steps;
  }

  set pickupSteps(steps: IGenericTaskStep[]) {
    this._steps = [...steps, ...this.dropoffSteps];
  }

  get pickupSteps(): IGenericTaskStep[] {
    if (!get(this._steps, 'length')) return [];
    return this._steps.filter((step) => step.isPickUp);
  }

  set dropoffSteps(steps: IGenericTaskStep[]) {
    this._steps = [...this.pickupSteps, ...steps];
  }

  get dropoffSteps(): IGenericTaskStep[] {
    if (!get(this._steps, 'length')) return [];
    return this._steps.filter((step) => !step.isPickUp);
  }

  setTrue: boolean = true;
  saveMapIcon: string = 'fa fa-files-o';

  constructor(
    private taskTypeService: TaskTypeService,
    private organisationSpecificStepService: OrganisationSpecificStepsService,
    private messageService: MessageService,
    private loggerService: LoggerService,
    private zone: NgZone,
  ) {
    const tag = 'component:organisation-specific-task-types';
    this.logger = loggerService.taggedLogger(tag);
    this.loader = Loader.create(tag, loggerService);
  }

  async ngOnInit(): Promise<void> {
    const taskId = this.loader.addTask('ngOnInit()');
    try {
      const response = await this.taskTypeService.get();
      if (!response) throw new Error('Could not get task types from task type service');

      // todo: remove the id from here, it's for debugging purposes only
      const mappedTaskTypes = response.map((taskType) => ({
        label: taskType.name,
        value: taskType.id,
      }));

      this.zone.run(() => {
        this.taskTypes = mappedTaskTypes;
        // Select the default
        if (!this.selectedTaskTypeId && this.selectedTaskTypeId !== 0 && this.taskTypes[0]) {
          this.selectedTaskTypeId = this.taskTypes[0].value;
        }
      });
    } catch (error) {
      this.logger.error('Could not init the component', error);
      // todo: let the user know
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  /**
   * Refreshes the checklist steps
   * todo: wrap assignments to the view model in ngZone calls
   */
  async refreshChecklistTaskTypeSteps(): Promise<void> {
    if (!(this.selectedChecklist && this.selectedChecklist.id && this.selectedTaskTypeId)) {
      this.logger.warn('refreshChecklistTaskTypeSteps() called but not ready to be called');
      return;
    }

    this._steps = [];

    const taskId = this.loader.addTask('refreshChecklistTaskTypeSteps()');

    try {
      // todo: should this really be being done here?
      this.zone.run(() => {
        // this._taskTypeEnabled = false;
      });
      const response = await this.organisationSpecificStepService.getChecklistTaskTypeStepDetail(
        this.selectedChecklist.id,
        this.selectedTaskTypeId,
        false,
      );

      this.zone.run(() => {
        this.checklistTaskTypeSteps = response;

        if (response && response.isActive != null) {
          this.zone.run(() => {
            this._taskTypeEnabled = response.isActive;
          });
        } else {
          this.zone.run(() => {
            this._taskTypeEnabled = false;
          });
        }
      });

      if (this.selectedChecklist.id) {
        const defaultChecklistSteps = await this.organisationSpecificStepService.getChecklistSteps(
          this.selectedChecklist.id,
        );

        //this.logger.debug("CHECKLIST STEPS: ", defaultChecklistSteps);

        const mappedTaskSteps =
          this.organisationSpecificStepService.buildTaskStepsChecklistStepList(
            this.selectedTaskTypeSteps,
            this.checklistTaskTypeSteps,
            defaultChecklistSteps,
          );

        //this.logger.debug("MAPPED STEPS: ", mappedTaskSteps);

        this.zone.run(() => {
          this.logger.debug('Steps Loaded:', mappedTaskSteps);
          this._steps = mappedTaskSteps;
        });
      }
    } catch (error) {
      this.logger.error('Could not refreshChecklistTaskTypeSteps()', error);
      // todo: let the user know?
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  /**
   * Saves the checklist mapping
   * @param auto if true auto apply to other checklists
   * @return Resolves When done
   */
  async saveChecklistMapping(auto: boolean = false): Promise<void> {
    this.saveMapIcon = 'fa fa-spinner fa-spin';

    const taskId = this.loader.addTask('saveChecklistMapping()');
    try {
      this.logger.trace('saveChecklistMapping()');
      await this.handleChecklistSave(auto);
      await this.refreshChecklistTaskTypeSteps();
      this.messageService.add(Message.Success('Mapping Saved', 'Success'));
    } catch (error) {
      this.logger.error('Error saving checklist mapping', error);
      this.messageService.add(
        Message.Error('An error occurred saving Mapping information', 'Error'),
      );
    } finally {
      this.loader.completeTask(taskId);
      this.saveMapIcon = 'fa fa-check';
    }
  }

  /**
   * This is what triggers the network request to post/put checklist (sorted)
   * @param autoApplyToOtherTaskTypes if true auto applies to other task types
   * @return resolves when done, todo: what with?
   * @private
   */
  private async handleChecklistSave(autoApplyToOtherTaskTypes: boolean = false): Promise<void> {
    this.logger.warn(`handleChecklistSave(${this.autoApplyChecklist})`);
    let startCount = 1;

    // get the pickup steps and drop off step, note their new positions and the reference mandatory step id
    const pMewPickupStepsOrder = this.getNewSteps(this.pickupSteps, startCount, StepType.StartTask);

    // sort dropoffSteps
    startCount = pMewPickupStepsOrder.length + 1;
    const dNewDropffStepsOrder = this.getNewSteps(
      this.dropoffSteps,
      startCount,
      StepType.EndMovement,
    );

    //send to server and save if happy
    const superObject = <IChecklistTaskTypeStep>{
      id: this.checklistTaskTypeSteps ? this.checklistTaskTypeSteps.id : 0,
      taskTypeId: this.selectedTaskTypeId,
      organisationSpecificStepGroupId: this.selectedChecklist.id,
      isActive: this.taskTypeEnabled,
      stepsTaskTypeDetails: <IChecklistTaskTypeStepDetail[]>[
        ...pMewPickupStepsOrder,
        ...dNewDropffStepsOrder,
      ],
    };
    // Updating Checklist Task Type Mapping
    if (superObject.id) {
      return this.organisationSpecificStepService.updateChecklistTaskTypeMapping(
        superObject,
        autoApplyToOtherTaskTypes,
      );
    } else {
      // Creating checklist task type mapping
      return this.organisationSpecificStepService.createChecklistTaskTypeMapping(
        superObject,
        autoApplyToOtherTaskTypes,
      );
    }
  }

  /**
   * Creates new steps for a new checklist
   * @param items The steps for this checklist
   * @param startCount A start count? I think because this method is called twice, first with pickup then with dropoff
   * @param mandatoryStepId The default step that checklist steps come after
   */
  getNewSteps(
    items: IGenericTaskStep[],
    startCount: number,
    mandatoryStepId: number,
  ): IChecklistTaskTypeStepDetail[] {
    const steps = items.reduce((results: IChecklistTaskTypeStepDetail[], val: any, index) => {
      if (!val.isEngineiusStep) {
        results.push(<IChecklistTaskTypeStepDetail>{
          id: val.isTaskTypeDetail ? val.id : 0,
          order: startCount, // todo: double check here, this should be order after
          parentEngineiusStepId: val.parentEngineiusStepId,
          isActive: val.isActive,
          isPickUp: val.isPickUp,
          organisationSpecificStepId: val.organisationSpecificStepId,
          organisationSpecificStepsTaskTypeId: val.organisationSpecificStepsTaskTypeId,
        });
        startCount++;
      }

      return results;
    }, []);
    this.logger.debug('Got New Steps:', steps);
    return steps;
  }

  /**
   * Scary generic method name, I wonder what it updates?
   * todo: Is this going to cause issues on different step types? Basically, check out the scenario in auto apply.
   * @param pickup If true, it's the pickup table, otherwise it's the dropoff table
   * @param event The row reorder event
   */
  async reorderSteps(pickup: boolean, event: ReorderEvent): Promise<void> {
    // note: when moving forward primeng messes up the to index by order of 1
    const adjusted_to = event.dropIndex;
    const taskId = this.loader.addTask(
      `reorderSteps(${pickup}, ${event.dragIndex}, ${adjusted_to})`,
    );

    this.logger.info(`reorderSteps(${pickup}, ${event.dragIndex}, ${adjusted_to})`);

    try {
      this._steps = this.reorderStepsAndUpdateParentId(pickup, event.dragIndex, adjusted_to);

      await this.updateSteps(this.selectedTaskTypeId, this.selectedChecklist.id, this._steps);
    } catch (error) {
      this.logger.error('Could not update rows', error);
      // todo: should probably let the user know
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  async updateSteps(taskTypeId: number, checklistId: number, steps: IGenericTaskStep[]) {
    await this.organisationSpecificStepService.updateSpecificStepsTaskStepDetail(
      taskTypeId,
      checklistId,
      steps,
    );
  }

  /**
   * When selectedTaskTypeId is updated we need to update steps.
   */
  private async onSelectedTaskTypeChange(): Promise<void> {
    this.logger.info('onSelectedTaskTypeChange():', this._selectedTaskTypeId);
    const newId = this._selectedTaskTypeId;
    const taskId = this.loader.addTask(`onSelectedTaskTypeChange(${newId})`);
    try {
      const result = await this.taskTypeService.getTaskTypeSteps(this.selectedTaskTypeId);
      this.zone.run(() => {
        this.selectedTaskTypeSteps = result;
      });
      await this.refreshChecklistTaskTypeSteps();
    } catch (error) {
      this.logger.error('Could not update task type');
      // todo: inform the user
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  private reorderStepsAndUpdateParentId(pickup: boolean, from: number, to: number) {
    // Copy the pickup/dropoff steps to a mutator (to make the changes distinct on re-assignment)
    const stepsToReorder = pickup ? [...this.pickupSteps] : [...this.dropoffSteps];

    const reordered = move(stepsToReorder, from, to);

    const updated = this.updateParentStepTypes(reordered);

    // Before putting back into the _steps array, make sure we bring the whole list back together
    // We haven't changed the opposite list's order so we're safe to just crack on.
    return pickup ? [...updated, ...this.dropoffSteps] : [...this.pickupSteps, ...updated];
  }

  /**
   * Updates the parent step ID on engineius step types, returning the new steps array
   * @param steps The steps in their newly sorted order
   * @return the updated array with parentStepTypes and order correctly set
   * @private
   */
  private updateParentStepTypes(steps: IGenericTaskStep[]): IGenericTaskStep[] {
    // ParentEngieniusStepID = the previous engineius step's step type id
    // Order: The number after that step that this step should be ordered in
    // Before, we have an ordered list of steps, some engineius, some custom
    // e.g.:
    // - Start Task: Engineius step
    // - Custom Step 1: Custom Step
    // - Custom Step 2: Custom Step
    // - Arrived At Pickup: Engineius Step
    // - Custom Step 3: Custom Step
    // - Custom Step 4: Custom Step
    // We need to group and sort, leading to this:
    // i.e.:
    // - Start Task: Step type = 1
    //   - Custom Step 1: Parent = 1, order = 0
    //   - Custom Step 2: Parent = 1, order = 1
    // - Arrived At Pickup: Step type = 2
    //   - Custom Step 3: Parent = 2, order = 0
    //   - Custom Step 4: Parent = 2, order = 1

    const updated: IGenericTaskStep[] = [...steps];

    let parentEngineiusStepTypeId;
    let order = 0;

    for (const step of updated) {
      if (step.isEngineiusStep) {
        // If it's an Engineius step, reset the parent step type id and order
        parentEngineiusStepTypeId = step.stepTypeId;
        order = 0;
        continue;
      }

      // Otherwise, set parentEngieniusStepId and increment order
      step.parentEngineiusStepId = parentEngineiusStepTypeId;
      step.order = order++;
    }

    return updated;
  }
}
